/**
 * 69.29 step 5 (ARCHITECTURE §5.25 (d); DECISIONS 245): the bots crewing a tank — what its gun loads, what its driver turns
 * the hull toward, and what its turret knows. Pure readers here (step 6's rocket rule, ai/armourAim.ts, reads the same zones);
 * the brain's half is ai/actionBot.ts (the gunner in crewGun, the driver in techDrive, the crew in technicalLoop's crews).
 */
import type { GameState, PlayerState, StructureState, TankRound, Vec3 } from '../types';
import { zoneTableOf } from '../core/vehicleZones';
import { drives, seatArmed, structureTeam } from '../core/body';
import { seatUnit } from '../core/seats';
import { brainFace } from '../core/brainFace';
import { arcDistance } from '../core/gameState';
import { carriesRocket } from '../core/rocketStock';
import { carriesPanzerfaust } from '../core/loadout';
import type { Belief } from '../core/picture';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';

/** How near a vehicle an enemy its side knows must be for the vehicle to count as known (people: its crew or its riders). */
export const TANK_KNOWN_PEOPLE = 4;

/**
 * The round the gun loads for an enemy vehicle (§5.25 (d): the solid round at a vehicle, the explosive one at a soft one): the
 * explosive at the soft class (the technical, the supply truck: its burst through a cab no plate stops; the bike, unzoned and
 * open, the same), the solid at the rest — the light, the armoured, the heavy, an unzoned hull.
 */
export function tankRoundFor(v: Pick<StructureState, 'type'>): TankRound {
  if (v.type === 'motorcycle') return 'he';
  return zoneTableOf(v)?.cls === 'soft' ? 'he' : 'solid';
}

/** The ids of the units a tank's turret knows by sight: its gunner's look and its commander's (§5.25 (d): "the commander's sighting is the turret's"), as each's brain held them at the tick's start. */
export function turretSightings(state: Pick<GameState, 'actionBrains'>, tank: Pick<StructureState, 'seats'>): Set<number> {
  const out = new Set<number>();
  for (const z of tank.seats ?? []) {
    const id = z.occupant ? seatUnit(z) : null;
    if (id === null) continue;
    for (const e of brainFace(state, id)?.seenIds ?? []) out.add(e);
  }
  return out;
}

/** The enemy hulls a tank's turret has in its sights: its crew's held hull targets (each's brain at the tick's start: `hullSeen`). The vehicles a crew sees: a crew inside one is seldom seen as a person. */
export function turretVehicles(state: Pick<GameState, 'actionBrains'>, tank: Pick<StructureState, 'seats'>): Set<number> {
  const out = new Set<number>();
  for (const z of tank.seats ?? []) {
    const id = z.occupant ? seatUnit(z) : null;
    const h = id !== null ? brainFace(state, id)?.hullSeen : undefined;   // (its face: which hull a crew fights is public)
    if (h !== undefined) out.add(h);
  }
  return out;
}

/**
 * What a tank's driver turns the hull's front toward, stopped (§5.25 (d): the hull's front its best face): of what its side
 * knows on the tank's planet, an enemy vehicle with a gun crewed (an armed seat held; known by an enemy its side knows within
 * TANK_KNOWN_PEOPLE of it — its crew or riders seen there — or a hull its turret has in its sights), else a unit carrying a rocket
 * or a Panzerfaust, else a hull its turret has in its sights (armed or not), else the nearest known enemy — each the nearest of its
 * kind. Null: nothing known.
 */
export function tankThreat(state: GameState, tank: StructureState, team: number, known: readonly Belief[], inSight: ReadonlySet<number> = turretVehicles(state, tank)): { planetIndex: number; surfaceDir: Vec3 } | null {
  const planet = state.planets[tank.planetIndex];
  if (!planet) return null;
  const R = planet.radius, d = (to: Vec3) => arcDistance(to, tank.surfaceDir, R), near = TANK_KNOWN_PEOPLE * PERSON_BODY_HEIGHT * personSetOf(state).k;
  let gun: StructureState | null = null;
  for (const v of state.structures) {
    if (v.id === tank.id || v.health <= 0 || v.planetIndex !== tank.planetIndex || !drives(v) || !(v.seats ?? []).some(z => seatArmed(z) && seatUnit(z) !== null)) continue;
    const vt = structureTeam(state, v);
    if (vt === null || vt === team) continue;
    if (!inSight.has(v.id) && !known.some(k => k.planetIndex === v.planetIndex && arcDistance(k.surfaceDir, v.surfaceDir, R) <= near)) continue;
    if (!gun || d(v.surfaceDir) < d(gun.surfaceDir) || (d(v.surfaceDir) === d(gun.surfaceDir) && v.id < gun.id)) gun = v;
  }
  if (gun) return { planetIndex: gun.planetIndex, surfaceDir: gun.surfaceDir };
  const here = known.filter(k => k.planetIndex === tank.planetIndex);
  const units = new Map<number, PlayerState>(state.players.map(p => [p.id, p]));
  const nearest = (list: readonly Belief[]) => list.reduce<Belief | null>((a, k) => (!a || d(k.surfaceDir) < d(a.surfaceDir) ? k : a), null);
  const rocket = nearest(here.filter(k => { const u = k.unitId !== undefined ? units.get(k.unitId) : undefined; return !!u && (carriesRocket(u, state.config) || carriesPanzerfaust(u)); }));
  if (rocket) return { planetIndex: rocket.planetIndex, surfaceDir: rocket.surfaceDir };
  // (a hull its turret is fighting, armed or not: the nearest)
  let hull: StructureState | null = null;
  for (const v of state.structures) if (inSight.has(v.id) && v.health > 0 && v.planetIndex === tank.planetIndex && (!hull || d(v.surfaceDir) < d(hull.surfaceDir))) hull = v;
  if (hull) return { planetIndex: hull.planetIndex, surfaceDir: hull.surfaceDir };
  const k = nearest(here);
  return k ? { planetIndex: k.planetIndex, surfaceDir: k.surfaceDir } : null;
}
